import { useIntl } from "react-intl";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { UserRoles } from "~enums/userRoles";
import UserPermissionArrayValueLabel from "~pages/Users/components/UserPermissionArrayValueLabel/UserPermissionArrayValueLabel.tsx";
import UserPermissionScopeValueLabel from "~pages/Users/components/UserPermissionScopeValueLabel/UserPermissionScopeValueLabel.tsx";
import { PermissionScopeTypes } from "~pages/Users/enums/permissionScopeTypes.ts";
import { UserPermissionsTableColumns } from "~pages/Users/enums/userPermissionsTableColumns.ts";
import type { LabelValue } from "~types/labelValue";
import { sortStringValues } from "~utils/utilities.ts";
import { PermissionProducts } from "../enums/permissionProducts";
import type { UserPermissionTransformed } from "../types";

const useGetUserPermissionsTableColumns = (product: PermissionProducts | undefined): DataTableColumn<UserPermissionTransformed>[] => {
  const { formatMessage } = useIntl();

  const columns: DataTableColumn<UserPermissionTransformed>[] = [
    {
      title: formatMessage({ id: "permissions.user.role" }),
      key: UserPermissionsTableColumns.UserRole,
      dataIndex: "userRole",
      width: 200,
      sorter: (a, b) => sortStringValues(a.userRole, b.userRole),
      render: (value: UserRoles) => formatMessage({ id: `permissions.userRole.${value}` }),
    },
  ];

  if (product === PermissionProducts.TeamManager) {
    columns.push(
      {
        title: formatMessage({ id: "entitygroups" }),
        key: UserPermissionsTableColumns.EntityGroups,
        dataIndex: "entityGroups",
        width: 300,
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) => (
          <UserPermissionScopeValueLabel value={value} userPermission={permission} />
        ),
      },
      {
        title: formatMessage({ id: "entities" }),
        key: UserPermissionsTableColumns.Entities,
        dataIndex: "entities",
        width: 300,
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) => (
          <UserPermissionScopeValueLabel value={value} userPermission={permission} />
        ),
      },
    );
  } else {
    columns.push(
      {
        title: formatMessage({ id: "leagues" }),
        key: UserPermissionsTableColumns.Leagues,
        dataIndex: "leagues",
        width: 200,
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) => (
          <UserPermissionScopeValueLabel value={value} userPermission={permission} />
        ),
      },
      {
        title: formatMessage({ id: "competitions" }),
        key: UserPermissionsTableColumns.Competitions,
        dataIndex: "competitions",
        width: 200,
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) =>
          permission.scope === PermissionScopeTypes.League ? (
            formatMessage({ id: "all" })
          ) : (
            <UserPermissionScopeValueLabel value={value} userPermission={permission} />
          ),
      },
      {
        title: formatMessage({ id: "seasons" }),
        key: UserPermissionsTableColumns.Seasons,
        dataIndex: "seasons",
        width: 200,
        render: (value: LabelValue[] | null, permission: UserPermissionTransformed) =>
          permission.scope === PermissionScopeTypes.League || permission.scope === PermissionScopeTypes.Competition ? (
            formatMessage({ id: "all" })
          ) : (
            <UserPermissionScopeValueLabel value={value} userPermission={permission} />
          ),
      },
      {
        title: formatMessage({ id: "season.statuses" }),
        key: UserPermissionsTableColumns.SeasonStatuses,
        dataIndex: "seasonStatuses",
        width: 150,
        sorter: (a, b) => sortStringValues(a.seasonStatuses?.join(", ") ?? "", b.seasonStatuses?.join(", ") ?? ""),
        render: (values: string[] | null) => <UserPermissionArrayValueLabel values={values} />,
      },
      {
        title: formatMessage({ id: "fixture.statuses" }),
        key: UserPermissionsTableColumns.FixtureStatuses,
        dataIndex: "fixtureStatuses",
        width: 150,
        sorter: (a, b) => sortStringValues(a.fixtureStatuses?.join(", ") ?? "", b.fixtureStatuses?.join(", ") ?? ""),
        render: (values: string[] | null) => <UserPermissionArrayValueLabel values={values} />,
      },
    );
  }

  return columns;
};

export default useGetUserPermissionsTableColumns;
